import { createFileRoute } from "@tanstack/react-router";
import { AtSign, Bot, Building2, Layers, PenLine, Send, ThumbsUp, Users } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/brand/empty-state";
import { ErrorState } from "@/components/brand/error-state";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AiUsagePanel,
  BreakdownChart,
  ClientComparison,
  Kpi,
  LastPosted,
  PipelineFunnel,
  ProgrammePanel,
  SetupBadges,
  TREND_TITLE,
  TrendChart,
  useCrossOrgReport,
  type Window,
  WindowPicker,
} from "@/features/analytics";
import type { OrgStat } from "@/lib/api";

/**
 * Cross-org analytics, for the Super Admin. Every client org rolled up.
 *
 * The same counts-only rule as the org report, one level up: totals across
 * all clients, and a line per client to see who is active and who has
 * stalled. Never a draft, a moment or a voice profile, at any level.
 *
 * Six views, in the order the questions get asked: how is it going, who is
 * carrying it, is it getting better, is the model's output being used, is
 * everyone set up to work at all, and what is the AI costing.
 */
export const Route = createFileRoute("/_authenticated/admin/analytics")({
  component: CrossOrgAnalyticsPage,
});

function CrossOrgAnalyticsPage() {
  const [window, setWindow] = useState<Window>("month");
  const { data, isPending, isFetching, isError, refetch } = useCrossOrgReport(window);

  return (
    <div>
      <PageHeader
        eyebrow="Super Admin"
        title="Cross-org analytics"
        description="Every client rolled up. Counts and public reactions only, never any draft."
      />

      {isPending ? (
        <Skeleton className="h-96 rounded-lg" />
      ) : isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : data.totals.orgs === 0 ? (
        <EmptyState
          title="No client orgs yet"
          description="Once clients are set up and their teams start posting, the numbers show up here."
        />
      ) : (
        <Tabs
          defaultValue="overview"
          className={`space-y-6 transition-opacity ${isFetching ? "pointer-events-none opacity-60" : ""}`}
        >
          {/* The view and the window are one decision, so they sit together. */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList className="flex-wrap">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="orgs">By client</TabsTrigger>
              <TabsTrigger value="trend">Trend</TabsTrigger>
              <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
              <TabsTrigger value="setup">Setup</TabsTrigger>
              <TabsTrigger value="ai">AI usage</TabsTrigger>
            </TabsList>
            <WindowPicker value={window} onChange={setWindow} />
          </div>

          <TabsContent value="overview" className="space-y-6">
            {/* Participation leads: whether people are posting is the question
                the programme exists to answer. Reactions describe how posts
                landed, which matters second. */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              <Kpi
                label="Posting"
                value={`${data.totals.activePosters} / ${data.totals.employees}`}
                hint="people across all teams"
                icon={<Users className="size-3.5" />}
              />
              <Kpi
                label="Active clients"
                value={`${data.totals.activeOrgs} / ${data.totals.orgs}`}
                hint="posting this window"
                icon={<Building2 className="size-3.5" />}
              />
              <Kpi
                label="Posts"
                value={data.totals.posts.toLocaleString()}
                hint="self-reported"
                icon={<Send className="size-3.5" />}
              />
              <Kpi
                label="Reactions"
                value={data.totals.reactions.toLocaleString()}
                hint={`${data.totals.comments.toLocaleString()} comments`}
                icon={<ThumbsUp className="size-3.5" />}
              />
              {/* Whether the training stuck: are people using the whole
                  framework, or posting one kind of thing every week. */}
              <Kpi
                label="Pillar coverage"
                value={
                  data.totals.pillarsUsedAvg != null && data.totals.pillarsDenominator
                    ? `${data.totals.pillarsUsedAvg.toFixed(1)} of ${data.totals.pillarsDenominator}`
                    : "-"
                }
                hint={
                  data.totals.pillarsUsedAvg != null
                    ? "pillars used on average"
                    : "No posts in this window yet"
                }
                icon={<Layers className="size-3.5" />}
              />
              {/* The outcome side of advocacy: is anyone, anywhere on
                  LinkedIn, talking about the clients. */}
              <Kpi
                label="Brand mentions"
                value={data.totals.mentions?.toLocaleString() ?? "0"}
                hint="posts on LinkedIn naming a client"
                icon={<AtSign className="size-3.5" />}
              />
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <ClientComparison rows={data.byOrg} />
              <BreakdownChart
                title="Reactions by client"
                data={data.byOrg.map((o) => ({
                  name: o.orgName,
                  reactions: o.reactions,
                  posts: o.posts,
                }))}
              />
            </div>
          </TabsContent>

          <TabsContent value="orgs">
            <OrgTable rows={data.byOrg} />
          </TabsContent>

          <TabsContent value="trend">
            <TrendChart data={data.trend} title={TREND_TITLE[window]} detail />
          </TabsContent>

          <TabsContent value="pipeline" className="space-y-6">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Kpi
                label="Prompts written"
                value={(data.pipeline?.[0]?.count ?? 0).toLocaleString()}
                hint="by the model, for employees"
                icon={<Bot className="size-3.5" />}
              />
              <Kpi
                label="Drafts started"
                value={(data.pipeline?.[1]?.count ?? 0).toLocaleString()}
                hint={data.pipeline?.[1]?.note}
                icon={<PenLine className="size-3.5" />}
              />
              <Kpi
                label="Posted"
                value={(data.pipeline?.[2]?.count ?? 0).toLocaleString()}
                hint={data.pipeline?.[2]?.note}
                icon={<Send className="size-3.5" />}
              />
              <Kpi
                label="Reactions earned"
                value={data.totals.reactions.toLocaleString()}
                hint={`${data.totals.comments.toLocaleString()} comments`}
                icon={<ThumbsUp className="size-3.5" />}
              />
            </div>
            {data.pipeline && data.pipeline.length > 0 ? (
              <PipelineFunnel stages={data.pipeline} />
            ) : null}
          </TabsContent>

          <TabsContent value="setup">
            {data.programme ? (
              <div className="grid gap-4 lg:grid-cols-2">
                <ProgrammePanel health={data.programme} />
                <OrgSetupTable rows={data.byOrg} />
              </div>
            ) : null}
          </TabsContent>

          <TabsContent value="ai">{data.ai ? <AiUsagePanel ai={data.ai} /> : null}</TabsContent>
        </Tabs>
      )}
    </div>
  );
}

/**
 * Two sources side by side, labelled so nobody reads them as one dataset.
 * Posting, posts and when someone last posted are what employees reported in
 * Compose. Reactions and comments are read from LinkedIn, off the link each
 * person pasted when they posted; they are not Company Page numbers.
 */
function OrgTable({ rows }: { rows: OrgStat[] }) {
  const group = "border-border border-l text-center text-xs uppercase tracking-wide";
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead />
            <TableHead colSpan={4} scope="colgroup" className={group}>
              Self-reported
            </TableHead>
            <TableHead
              colSpan={3}
              scope="colgroup"
              className={group}
              title="Read from LinkedIn off each post's link, not from the Company Page"
            >
              From LinkedIn posts
            </TableHead>
            <TableHead
              scope="colgroup"
              className={group}
              title="Posts by anyone on LinkedIn that name the client"
            >
              Across LinkedIn
            </TableHead>
          </TableRow>
          <TableRow>
            <TableHead>Client</TableHead>
            <TableHead className="border-border border-l text-right">Posting</TableHead>
            <TableHead className="text-right">Posts</TableHead>
            <TableHead className="text-right">Last posted</TableHead>
            <TableHead
              className="text-right"
              title="Distinct content pillars posted under, of the client's own"
            >
              Pillars
            </TableHead>
            <TableHead className="border-border border-l text-right">Reactions</TableHead>
            <TableHead className="text-right">Comments</TableHead>
            <TableHead className="text-right">Avg per post</TableHead>
            <TableHead className="border-border border-l text-right">Mentions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const quiet = r.posts === 0;
            return (
              <TableRow key={r.orgName} className={quiet ? "text-muted-foreground" : undefined}>
                <TableCell className="font-medium text-foreground">
                  {r.orgName}
                  {quiet ? (
                    <Badge variant="secondary" className="ml-2">
                      Not posting
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell className="border-border border-l text-right tabular-nums">
                  {r.activePosters} / {r.team}
                </TableCell>
                <TableCell className="text-right tabular-nums">{r.posts}</TableCell>
                <TableCell className="whitespace-nowrap text-right text-xs">
                  <LastPosted at={r.lastPostedAt} />
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.pillarsUsed == null ? (
                    <span className="text-muted-foreground text-xs" title="No posts in this window">
                      No posts
                    </span>
                  ) : (
                    `${r.pillarsUsed}/${r.pillarsTotal}`
                  )}
                </TableCell>
                <TableCell className="border-border border-l text-right tabular-nums">
                  {r.reactions}
                </TableCell>
                <TableCell className="text-right tabular-nums">{r.comments}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.posts ? (r.avgReactions ?? 0) : "-"}
                </TableCell>
                <TableCell className="border-border border-l text-right tabular-nums">
                  {r.mentions ?? 0}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/** The same clients, read for whether they are set up rather than for output. */
function OrgSetupTable({ rows }: { rows: OrgStat[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Client</TableHead>
            <TableHead>What is missing</TableHead>
            <TableHead className="text-right">Team</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const ready =
              r.approvalStatus === "approved" &&
              r.pageStatus === "connected" &&
              r.team > 0 &&
              (r.voiceReady ?? 0) >= r.team;
            return (
              <TableRow key={r.orgName}>
                <TableCell className="font-medium text-foreground">{r.orgName}</TableCell>
                <TableCell>
                  {ready ? <Badge variant="success">Fully set up</Badge> : <SetupBadges row={r} />}
                </TableCell>
                <TableCell className="text-right tabular-nums">{r.team}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
